import { useTranslation } from "react-i18next";
import {
  AutoComplete,
  AutoCompleteMulti,
  FormContainer,
  ISelectType,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import { calendarRegisterList } from "../../../../../../../services/ticketAutomationReceivedSlotCalendar.service";
import {
  createScheduleFilterUser,
  getScheduleFilterUserById,
  updateScheduleFilterUserById,
} from "../../../../../../../services/scheduleFilterUser.service";
import {
  scheduleFilterUserSchema,
  TScheduleFilterUserForm,
} from "./scheduleFilterUserSchema";
import UsersService from "../../../../../../../services/users.service";

const translationPath = "page.settings.schedule.scheduleFilterUser.";

const usersService = new UsersService();
const ScheduleFilterUser = () => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createScheduleFilterUser
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateScheduleFilterUserById
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getScheduleFilterUserById
  );

  const [optionsScheduleFilterUserCalendars, setScheduleFilterUserCalendars] =
    useState<ISelectType[]>([]);

  const [options, setOptions] = useState<ISelectType[]>([]);

  const { sendRequest } = useFetch(calendarRegisterList);

  const { sendRequest: usersRequest } = useFetch(usersService.list);

  const updateHandler = (v: TScheduleFilterUserForm, id: string) =>
    update({
      item: {
        id: id,
        ...v,
        userId: v.user.id,
        scheduleFilterUserCalendars: v.scheduleFilterUserCalendars.map(
          (cal) => ({
            externalId: cal.id,
            externalName: cal.name,
            scheduleTypeCode: cal.code,
          })
        ),
      },
      id: id,
    });

  const saveHandler = (v: TScheduleFilterUserForm) =>
    save({
      ...v,
      userId: v.user.id,
      scheduleFilterUserCalendars: v.scheduleFilterUserCalendars.map((cal) => ({
        externalId: cal.id,
        externalName: cal.name,
        scheduleTypeCode: cal.code,
      })),
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TScheduleFilterUserForm>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: TScheduleFilterUserForm = {
          ...data,
          user: { id: data.user.id, name: data.user.fullName },
          scheduleFilterUserCalendars: data.scheduleFilterUserCalendars.map(
            (cal) => ({
              id: cal.externalId,
              name: cal.externalName,
              code: cal.scheduleTypeCode,
            })
          ),
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get]
  );

  const params = useMemo(() => {
    return {
      EnableCode: true,
      TasyScheduleTypeCodeList: [2, 3],
    };
  }, []);

  const fetchScheduleFilterUserCalendars = useCallback(async () => {
    const { data, success } = await sendRequest(params);

    if (data && success) {
      setScheduleFilterUserCalendars(
        data.map((d) => {
          const titleBase =
            d.tasyScheduleTypeCode === 3
              ? d.descriptionSite ?? d.calendarName
              : d.calendarName;

          return {
            id: String(d.identifier),
            name: `${titleBase} - ${d.identifier}`,
            code: d.tasyScheduleTypeCode.toString(),
          };
        })
      );
    }
  }, [sendRequest, params]);

  useEffect(() => {
    fetchScheduleFilterUserCalendars();
  }, [fetchScheduleFilterUserCalendars]);

  const fetchUsers = useCallback(async () => {
    const { data, success } = await usersRequest(null);

    if (data && success) {
      setOptions(
        data.map((u: any) => ({
          id: u.id,
          name: u.fullName,
        }))
      );
    }
  }, [usersRequest]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TScheduleFilterUserForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={
        t(translationPath + "schedule_filter_user") || "Schedule Filter User"
      }
      schema={scheduleFilterUserSchema}
      subtitle={
        t(translationPath + "new_schedule_filter_user") ||
        "New Schedule Filter User"
      }
      fields={() => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <AutoComplete
              getOptionLabel={(option) => option.name}
              name="user"
              label={t(translationPath + "user")}
              size="small"
              options={options}
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <AutoCompleteMulti
              params={params}
              fullWidth
              options={optionsScheduleFilterUserCalendars}
              name="scheduleFilterUserCalendars"
              label={t(translationPath + "scheduleFilterUserCalendars")}
              getOptionLabel={(option) => option.name}
              size="small"
              keepOpenOnSelect={true}
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default ScheduleFilterUser;
